import type {
  DataProvider,
  HttpProvider,
  SqlProvider,
} from '../../../shared/types';

/**
 * Pattern to match ${variable} placeholders
 * Matches Java implementation: \$\{([^}]+)\}
 */
const PLACEHOLDER_PATTERN = /\$\{([^}]+)\}/g;

/**
 * Extracts placeholder variables from a string
 */
const extractPlaceholders = (text: string | undefined | null): string[] => {
  if (!text) {
    return [];
  }

  const matches: string[] = [];
  let match;

  // Reset lastIndex to ensure pattern works correctly
  PLACEHOLDER_PATTERN.lastIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = PLACEHOLDER_PATTERN.exec(text)) !== null) {
    matches.push(match[1]);
  }

  return matches;
};

/**
 * Extracts dependencies from SQL DataProvider
 */
const extractSqlDependencies = (sqlConfig: SqlProvider['sql']): string[] => {
  const dependencies: string[] = [];

  if (sqlConfig.where) {
    dependencies.push(...extractPlaceholders(sqlConfig.where));
  }

  if (sqlConfig.postWhere) {
    dependencies.push(...extractPlaceholders(sqlConfig.postWhere));
  }

  return dependencies;
};

/**
 * Extracts dependencies from HTTP DataProvider
 */
const extractHttpDependencies = (
  httpConfig: HttpProvider['httpApi']
): string[] => {
  const dependencies: string[] = [];

  // Extract from resource URL
  dependencies.push(...extractPlaceholders(httpConfig.resourceUrl));

  // Extract from query parameters
  if (httpConfig.queryParams) {
    httpConfig.queryParams.forEach((param) => {
      const { value } = param;

      // Handle both ${var} and plain var formats
      if (typeof value === 'string') {
        if (value.startsWith('${') && value.endsWith('}')) {
          dependencies.push(value.substring(2, value.length - 1));
        } else {
          dependencies.push(...extractPlaceholders(value));
        }
      }
    });
  }

  // Extract from request body
  if (httpConfig.body) {
    try {
      const bodyJson = JSON.stringify(httpConfig.body);

      dependencies.push(...extractPlaceholders(bodyJson));
    } catch (e) {
      // Ignore JSON stringify errors
    }
  }

  return dependencies;
};

/**
 * Extracts dependencies from DataProvider configuration
 * Matches Java implementation in CustomFormMapper.java
 *
 * @param dataProvider - The data provider to extract dependencies from
 * @returns Record with dependency names as keys and empty arrays as values
 */
export const extractDependencies = (
  dataProvider: DataProvider
): Record<string, string[]> => {
  const dependencyNames: string[] = [];

  switch (dataProvider.type) {
    case 'SQL':
      dependencyNames.push(...extractSqlDependencies(dataProvider.sql));
      break;

    case 'HTTP':
      dependencyNames.push(...extractHttpDependencies(dataProvider.httpApi));
      break;

    default:
      // FIXED and url types don't need extraction
      break;
  }

  // Convert to Record with empty arrays (matching Java's List.of())
  const dependencies: Record<string, string[]> = {};

  dependencyNames.forEach((name) => {
    dependencies[name] = [];
  });

  return dependencies;
};

/**
 * Processes all dataProviders and adds dependencies if not already present
 * Only processes when in preview mode
 *
 * @param dataProviders - Record of data providers
 * @param isPreview - Whether in preview mode
 * @returns Processed data providers with dependencies
 */
export const processDataProvidersForPreview = (
  dataProviders: Record<string, DataProvider>,
  isPreview: boolean
): Record<string, DataProvider> => {
  if (!isPreview) {
    return dataProviders;
  }

  const processed: Record<string, DataProvider> = {};

  Object.entries(dataProviders).forEach(([key, provider]) => {
    // Only extract if dependencies are not already set or empty
    if (
      !provider.dependencies ||
      Object.keys(provider.dependencies).length === 0
    ) {
      processed[key] = {
        ...provider,
        dependencies: extractDependencies(provider),
      };
    } else {
      processed[key] = provider;
    }
  });

  return processed;
};
